import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Mic, MicOff, Square, TriangleAlert, Info } from "lucide-react";
import {
  canRecord,
  pickMimeType,
  getVoiceStatus,
  transcribe,
  appendHeard,
  undoAppend,
  explainerSeen,
  markExplainerSeen,
} from "../../lib/voice";

/**
 * Issue #851 (epic #847, voice wave V2) — the one dictation component every
 * long box uses. Wired into the saved step's opened reflection first
 * (`idPrefix="reflect-mic"`); the Reflections tab (#853) reuses it under
 * its own box prefixes.
 *
 * Drawing: `SavedVoice.dc.html` / `VoiceFirstUse.dc.html`
 * (design-baseline-round-6-voice, sha256 `b147b25b…` / `a9627811…`).
 * Spec: `voice-design-2026-09-25.md` §§ 2, 4, 5, 6, 7, 9. Every string below
 * is copied from § 5 verbatim; every id from § 6.
 *
 * D112/D115 — the recording lives ONLY in this component's own memory
 * (`useRef`, never IndexedDB/localStorage/the Cache API/a service worker):
 * `frontend/src/lib/voice.js`'s own header states the same guarantee for
 * its half.
 */

const MAX_CLIP_S = 90; // spec § 9 — auto-stop, then transcribe (state 11)
const WARN_AT_S = 75; // spec § 9 — state 4a "near-limit"
const SLOW_AFTER_MS = 8000; // spec § 9 — state 6a "transcribing-slow"
const GIVE_UP_AFTER_MS = 20000; // D117 — state 10a "failed-too-slow", then abort
const AUDIO_BITS_PER_SECOND = 32000; // spec § 7 (recommended, not measured)

// "Only one box records at a time" (spec § 2) — a module-level store, NOT
// component state, because the rule is across every `DictationControl`
// mounted on the page, not just siblings that happen to share a parent.
let activeInstanceId = null;
const activeListeners = new Set();
function claimActive(id) {
  activeInstanceId = id;
  activeListeners.forEach((fn) => fn(activeInstanceId));
}
function releaseActive(id) {
  if (activeInstanceId === id) {
    activeInstanceId = null;
    activeListeners.forEach((fn) => fn(activeInstanceId));
  }
}
function subscribeActive(fn) {
  activeListeners.add(fn);
  return () => activeListeners.delete(fn);
}
let instanceCounter = 0;
function nextInstanceId() {
  instanceCounter += 1;
  return `dc-${instanceCounter}`;
}

function fmtClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// #1125 — the notice's Back returns to the screen this explainer was opened
// on. A plain `<a>` (a full page load, no router state), so the origin rides
// in `?from=`; the notice reads it through `safeRedirectTarget`. Path AND
// query: on `/development` the tab IS the query (`?tab=reflections`), so
// dropping it returned people to the Plan tab. The plain `<a>` is kept on its
// merits: this is a shared kit control that takes no router context, and a
// full load of the public, chromeless notice is what it already did.
function privacyHref() {
  const here = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
  return here && !here.startsWith("/privacy") ? `/privacy?from=${encodeURIComponent(here)}` : "/privacy";
}

function prefersReducedMotion() {
  try {
    return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

// The kinds `runTranscribe` keeps the blob for (its own `result.kind ===
// "network" || ... "rate_limited"` branch) — kept in sync with THAT
// branch, not re-derived, so the two can never drift apart.
const RETRYABLE_OUTCOMES = new Set(["network", "server", "too_slow", "rate_limited"]);

// R3-F1 (PR #969 review round 3) — pulled out of the component body
// (eslint's `max-lines-per-function` budget, #432): never hide while a
// request this component itself started (`activeNow`) is live, or U4's
// second recording (started while the first transcript's status
// re-check is still pending) would lose its Stop button the moment
// that re-check resolves unavailable. Round 4 REMOVED a client-side
// pre-empt that used to sit in `runTranscribe` and skip calling
// `transcribe()` outright once this control knew status was
// unavailable: it silently dropped the player's words on ANY transient
// status error (a network blip reads identically to a genuine
// unavailable here) and swallowed "Send it again" the same way after a
// failed send. The server decides now — 403 maps to `unavailable`, a
// transient error maps to `network` (state 10, blob kept) — and this
// guard alone is what keeps the player in control either way: Stop
// stays visible and pressing it (or "Send it again") is their choice.
// Round 5 — `activeNow` ALSO covers a retryable outcome (W5): the same
// stale/transient `localStatus` that must not hide a live recording
// must not hide "Send it again" either, once a network-shaped failure
// lands while that status is unavailable. The mic showing beside "Send
// it again" in that state is acceptable — it is not a NEW recording,
// just this control staying visible.
function deriveVisibility(phase, localStatus, recordable, outcomeKind) {
  const micOffDaily = !!(localStatus && localStatus.available === false && localStatus.reason === "daily_limit");
  const hiddenEntirely = !recordable || (localStatus && localStatus.available === false && !micOffDaily) || phase === "hidden";
  const activeNow =
    phase === "recording" || phase === "permission" || phase === "transcribing" || (phase === "outcome" && RETRYABLE_OUTCOMES.has(outcomeKind));
  return { micOffDaily, hiddenEntirely, activeNow };
}

const navyIconStyle = { color: "var(--nw-navy)", flexShrink: 0 };
const helpTextStyle = { fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" };
const noteBoxStyle = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  padding: "10px 12px",
  background: "var(--nw-line-faint, #E8F1FB)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--nw-ink)",
};
const alertNoteBoxStyle = {
  ...noteBoxStyle,
  background: "var(--nw-line-faint, #F1F5F9)",
  borderLeft: "3px solid var(--nw-alert)",
};

/**
 * `idPrefix` — the box's own prefix (`"reflect-mic"` on the saved step).
 * `boxLabel` — names the box for the screen-reader label where a screen has
 * several boxes.
 * `status` — the parent's own `getVoiceStatus()` result, fetched once per
 * screen (not per box).
 * `maxLength` — the box's own character cap (D126); the saved step passes
 * `8000`.
 * `onRecordingChange`/`onTranscribingChange` — bubble the two booleans
 * Save's own `disabled` needs (D117/D157).
 *
 * Exposes `discard()` through the ref (D157) — the parent's "Not now" calls
 * it before closing, so a recording or an in-flight request is dropped
 * rather than silently orphaned.
 */
const DictationControl = forwardRef(function DictationControl(
  { idPrefix, boxLabel, value, onChange, textareaRef, onTranscribingChange, onRecordingChange, status, maxLength = Infinity },
  ref
) {
  const instanceIdRef = useRef(nextInstanceId());
  const [otherActive, setOtherActive] = useState(activeInstanceId !== null && activeInstanceId !== instanceIdRef.current);

  // M-F1 (PR #969 review) — `recorder.onstop` is assigned ONCE, at the
  // moment recording starts, and calls `runTranscribe` through that
  // closure. `runTranscribe` used to read the `value` PROP directly, which
  // is exactly the render that was live when recording started — every
  // keystroke typed during recording or transcribing landed in the
  // PARENT's state (via `onChange`) but was invisible to that stale
  // closure, so appending the transcript overwrote it outright. Read
  // `valueRef.current` instead everywhere inside `runTranscribe`: a plain
  // ref assigned unconditionally on every render always holds the latest
  // value, regardless of which render's closure is doing the reading.
  const valueRef = useRef(value);
  valueRef.current = value;
  // Distinguishes a value change WE caused (inserting/appending a
  // transcript, or Undo) from one the PLAYER caused by typing — the
  // overflow note (M-F4) and the heard-nothing note (U6) both need to
  // clear on the player's own typing, but must not clear themselves the
  // instant our own `onChange` call lands. A TOKEN, not the value itself:
  // comparing the new value against "the value we last wrote" is fooled
  // the moment the player happens to type something that lands on the
  // exact same string (PR #969 review's own V4 probe does exactly this on
  // purpose) — a flag that means "the very next value-change is one we
  // caused, whatever it contains" isn't.
  const pendingOwnWriteRef = useRef(false);

  const [phase, setPhase] = useState("idle"); // idle | explainer | permission | denied | recording | transcribing | outcome | hidden
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [outcomeKind, setOutcomeKind] = useState(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [slowTranscribing, setSlowTranscribing] = useState(false);
  const [overflowed, setOverflowed] = useState(false);
  const [lastInserted, setLastInserted] = useState(null);
  const [localStatus, setLocalStatus] = useState(status || { available: false });
  const [autoStoppedAtLimit, setAutoStoppedAtLimit] = useState(false);

  // A plain ref (not state) so the interval/onstop closures below always
  // read the latest elapsed value without a stale-closure bug.
  const elapsedSRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null); // held in memory only (D115) — for "Send it again"
  const abortControllerRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const slowTimeoutRef = useRef(null);
  const giveUpTimeoutRef = useRef(null);
  const stopBtnRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [levels, setLevels] = useState([4, 4, 4, 4, 4, 4, 4, 4]);

  // Follows the parent's own `status` prop (it fetches once per screen,
  // but that fetch can genuinely resolve AFTER this control's first
  // render) — UNTIL this control does its own status re-check following a
  // successful transcript (below). From that point on, this control owns
  // the answer: the parent never re-fetches mid-session today, and a
  // stale/decorative prop change must never clobber a daily-limit
  // discovery this control only just made.
  const statusOverriddenRef = useRef(false);
  useEffect(() => {
    if (!statusOverriddenRef.current) {
      setLocalStatus(status || { available: false });
    }
  }, [status]);

  useEffect(() => {
    const unsubscribe = subscribeActive((id) => {
      setOtherActive(id !== null && id !== instanceIdRef.current);
    });
    return unsubscribe;
  }, []);

  const clearTimers = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (slowTimeoutRef.current) clearTimeout(slowTimeoutRef.current);
    if (giveUpTimeoutRef.current) clearTimeout(giveUpTimeoutRef.current);
    timerIntervalRef.current = null;
    slowTimeoutRef.current = null;
    giveUpTimeoutRef.current = null;
  };

  const stopLevelMeter = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try {
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch {
      // best-effort cleanup only
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  const stopStreamTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // M-F2/M-F3 (PR #969 review) — `requestMicrophone` awaits the OS
  // permission prompt, which can take an arbitrary amount of real time.
  // Two things can happen while it's pending, and neither used to be
  // checked after the `await`:
  //   - the component unmounts (the player navigates away) — `aliveRef`
  //     answers that;
  //   - "Not now" calls `discard()` while the SAME component is still
  //     mounted — `requestGenRef` answers that: `discard()`, and every
  //     fresh call to `requestMicrophone` itself, bumps it, so a request
  //     started before the bump can recognise it is no longer current.
  // Either way, the fix is the same: stop the stream this component never
  // asked to keep, and never start recording or call `transcribe` for a
  // request nobody is waiting on any more.
  const aliveRef = useRef(true);
  const requestGenRef = useRef(0);

  // Unmount, or the page is left: stop recording, stop every track, drop
  // the blob, abort any in-flight request. Nothing is sent after unmount
  // (spec § 2).
  useEffect(() => {
    const ownId = instanceIdRef.current;
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      requestGenRef.current += 1;
      clearTimers();
      stopLevelMeter();
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.onstop = null;
          mediaRecorderRef.current.stop();
        }
      } catch {
        // best-effort
      }
      stopStreamTracks();
      if (abortControllerRef.current) abortControllerRef.current.abort();
      blobRef.current = null;
      releaseActive(ownId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startLevelMeter = (stream) => {
    if (prefersReducedMotion()) return; // spec § 4 — no meter transitions under reduced motion
    try {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const bars = [];
        const step = Math.max(1, Math.floor(data.length / 8));
        for (let i = 0; i < 8; i += 1) {
          bars.push(4 + Math.round((data[i * step] || 0) / 16));
        }
        setLevels(bars);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      // The meter is decorative (aria-hidden) — never let it break recording.
    }
  };

  const clearOutcomeNotes = () => {
    setOutcomeKind(null);
    setOverflowed(false);
    setAutoStoppedAtLimit(false);
  };

  const runTranscribe = async (blob, seconds) => {
    // Round 4 (PR #969 review) — no client-side pre-empt here. A round-3
    // guard used to skip the network call outright once this control
    // knew status was unavailable; it read that as "any transient error
    // too" (the same shape `getVoiceStatus` maps a network blip to) and
    // silently dropped the player's words with no message, on this call
    // AND on "Send it again" after a failed send. Let the server decide:
    // 403 -> `unavailable` (drop the blob, hide, as drawn); a transient
    // error -> `network` (state 10, blob kept, the player's own choice
    // to retry). `deriveVisibility`'s `activeNow` is what actually
    // closes the privacy hole (R3-F1) — Stop stays visible regardless.
    setPhase("transcribing");
    setSlowTranscribing(false);
    if (onTranscribingChange) onTranscribingChange(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    slowTimeoutRef.current = setTimeout(() => setSlowTranscribing(true), SLOW_AFTER_MS);
    giveUpTimeoutRef.current = setTimeout(() => controller.abort(), GIVE_UP_AFTER_MS);

    const result = await transcribe(blob, seconds, controller.signal);

    // PR #979 review, F1 hardening — the unmount guard must not depend on
    // the transport actually honouring `controller.abort()`: a mocked or
    // otherwise non-conforming `transcribe()` can resolve/reject AFTER this
    // component has already unmounted (issue #853's own remount-per-target
    // fix relies on exactly that timing). Without this, a stale closure's
    // `onChange`/`setState` calls below would land on whichever component
    // now occupies this slot — the cross-target leak R1–R4 named. `return`
    // here, before anything else touches state or calls a prop callback:
    // nothing this instance does past its own unmount should be observable.
    if (!aliveRef.current) return;

    clearTimers();
    abortControllerRef.current = null;
    if (onTranscribingChange) onTranscribingChange(false);

    if (result.kind === "text") {
      blobRef.current = null;
      const currentValue = valueRef.current;
      const { value: nextValue, inserted, overflowed: didOverflow } = appendHeard(currentValue || "", result.text, maxLength);
      const wasEmpty = !currentValue || currentValue.trim() === "";
      // R3-F2 — only arm the token when the write actually changes the
      // value (a full 8,000-char box plus a fully-cut transcript is a
      // no-op write); React never re-renders for an unchanged value, so
      // an unconditionally-armed token goes unconsumed and swallows the
      // player's next real keystroke instead.
      pendingOwnWriteRef.current = nextValue !== (currentValue || "");
      onChange(nextValue);
      setLastInserted(inserted);
      setOverflowed(didOverflow);
      setOutcomeKind(wasEmpty ? "inserted" : "appended");
      setPhase("outcome");
      // Focus back to the box, caret at the end of the inserted words
      // (spec § 2 "Rules across states" — Focus).
      requestAnimationFrame(() => {
        const el = textareaRef && textareaRef.current;
        if (el && el.focus) {
          el.focus();
          if (typeof el.setSelectionRange === "function") {
            el.setSelectionRange(nextValue.length, nextValue.length);
          }
        }
      });
      // Item 2 (executor spec) — after every successful transcript, check
      // the status again; a fresh daily_limit turns the mic off and shows
      // 11b's note, without ever having stopped the clip early (D150).
      const fresh = await getVoiceStatus();
      statusOverriddenRef.current = true;
      setLocalStatus(fresh);
      return;
    }

    if (result.kind === "empty") {
      blobRef.current = null;
      setOutcomeKind("empty");
      setPhase("outcome");
      return;
    }

    if (result.kind === "network" || result.kind === "server" || result.kind === "too_slow" || result.kind === "rate_limited") {
      // The blob is KEPT for "Send it again" (D115 for network/server/
      // too_slow; the executor spec extends the same treatment to
      // rate_limited).
      setOutcomeKind(result.kind);
      setPhase("outcome");
      return;
    }

    if (result.kind === "too_long") {
      blobRef.current = null;
      setOutcomeKind("too_long_server");
      setPhase("outcome");
      return;
    }

    if (result.kind === "daily_limit") {
      // Rare race: the reservation itself was refused mid-flight, so
      // nothing was transcribed this time. Go straight to "off" rather
      // than claim words are in the box that were never heard.
      blobRef.current = null;
      statusOverriddenRef.current = true;
      setLocalStatus({ available: false, reason: "daily_limit" });
      setOutcomeKind(null);
      setPhase("idle");
      return;
    }

    // unavailable — hide the mic entirely from here on.
    blobRef.current = null;
    setPhase("hidden");
  };

  const beginRecording = async (stream) => {
    claimActive(instanceIdRef.current);
    streamRef.current = stream;
    chunksRef.current = [];
    setAutoStoppedAtLimit(false);
    clearOutcomeNotes();
    const mimeType = pickMimeType();
    let recorder;
    try {
      recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND })
        : new window.MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BITS_PER_SECOND });
    } catch {
      recorder = new window.MediaRecorder(stream);
    }
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      releaseActive(instanceIdRef.current);
      stopLevelMeter();
      stopStreamTracks();
      const seconds = elapsedSRef.current;
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || "audio/webm" });
      chunksRef.current = [];
      blobRef.current = blob;
      runTranscribe(blob, seconds);
    };
    recorder.start();
    startLevelMeter(stream);
    setPhase("recording");
    if (onRecordingChange) onRecordingChange(true);
    setElapsedS(0);
    elapsedSRef.current = 0;
    timerIntervalRef.current = setInterval(() => {
      elapsedSRef.current += 1;
      setElapsedS(elapsedSRef.current);
      if (elapsedSRef.current >= MAX_CLIP_S) {
        setAutoStoppedAtLimit(true);
        stopRecording();
      }
    }, 1000);
    requestAnimationFrame(() => stopBtnRef.current && stopBtnRef.current.focus());
  };

  const stopRecording = () => {
    clearTimers();
    if (onRecordingChange) onRecordingChange(false);
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // best-effort
    }
  };

  const deleteWhileRecording = () => {
    clearTimers();
    stopLevelMeter();
    if (onRecordingChange) onRecordingChange(false);
    try {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.onstop = null;
        if (mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
      }
    } catch {
      // best-effort
    }
    stopStreamTracks();
    releaseActive(instanceIdRef.current);
    chunksRef.current = [];
    blobRef.current = null;
    setPhase("idle");
  };

  const requestMicrophone = async () => {
    const myGen = (requestGenRef.current += 1);
    setPhase("permission");
    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({ audio: true });
      // M-F2/M-F3 — the player left, or "Not now" discarded this request,
      // while the OS prompt was pending. Stop the stream we just got and
      // never record: nothing this component wasn't asked for gets kept,
      // and nothing reaches `transcribe` for a request nobody is
      // waiting on.
      if (!aliveRef.current || requestGenRef.current !== myGen) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      markExplainerSeen();
      await beginRecording(stream);
    } catch {
      if (aliveRef.current && requestGenRef.current === myGen) setPhase("denied");
    }
  };

  const onMicPress = () => {
    if (otherActive) return;
    if (!explainerSeen()) {
      setPhase("explainer");
      return;
    }
    requestMicrophone();
  };

  const onExplainerContinue = () => {
    requestMicrophone();
  };

  const onExplainerCancel = () => {
    setShowPrivacy(false);
    setPhase("idle");
  };

  const onDeniedTryAgain = () => {
    setPhase("explainer");
  };

  const onUndo = () => {
    if (!lastInserted) return;
    const undone = undoAppend(valueRef.current || "", lastInserted);
    // R3-F2 — same guard as the insert site above: a no-op Undo must not
    // arm a token nothing will ever consume.
    pendingOwnWriteRef.current = undone !== (valueRef.current || "");
    onChange(undone);
    setLastInserted(null);
    setOverflowed(false);
    setOutcomeKind(null);
    setPhase("idle");
  };

  const onSendAgain = () => {
    if (!blobRef.current) return;
    const blob = blobRef.current; // SAME blob — object identity, never re-recorded.
    setOutcomeKind(null);
    runTranscribe(blob, elapsedSRef.current);
  };

  const onDiscardFailed = () => {
    blobRef.current = null;
    setOutcomeKind(null);
    setPhase("idle");
  };

  useImperativeHandle(ref, () => ({
    discard() {
      // M-F3 — invalidates any `requestMicrophone` still waiting on the
      // OS prompt: it checks this generation after its `await` and bails
      // out rather than starting a recording "Not now" already discarded.
      requestGenRef.current += 1;
      clearTimers();
      stopLevelMeter();
      try {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.onstop = null;
          if (mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
        }
      } catch {
        // best-effort
      }
      stopStreamTracks();
      if (abortControllerRef.current) abortControllerRef.current.abort();
      releaseActive(instanceIdRef.current);
      chunksRef.current = [];
      blobRef.current = null;
      if (onRecordingChange) onRecordingChange(false);
      if (onTranscribingChange) onTranscribingChange(false);
      setPhase("idle");
      clearOutcomeNotes();
    },
  }));

  // Escape stops and transcribes (never deletes) while recording — spec § 4.
  useEffect(() => {
    if (phase !== "recording") return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stopRecording();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // M-F4/U6 (PR #969 review) — the overflow note (D126) and the
  // heard-nothing note (spec § 2, state 9) both clear "when the player
  // types [in the box], talks again, or presses Undo". Talking again and
  // Undo already clear them directly (`beginRecording`'s
  // `clearOutcomeNotes()`, `onUndo`'s own resets) — this effect is the
  // third leg: the player typing over the box THEMSELVES. It fires on
  // every `value` change, but only acts when the change did NOT come from
  // this control's own `onChange` call (`pendingOwnWriteRef`, armed at
  // every site that calls `onChange`, disarmed the first time this effect
  // sees ANY change afterward) — otherwise the note would clear itself
  // the instant a transcript landed, before the player ever saw it.
  const prevValueRef = useRef(value);
  useEffect(() => {
    const changed = prevValueRef.current !== value;
    prevValueRef.current = value;
    if (!changed) return;
    if (pendingOwnWriteRef.current) {
      pendingOwnWriteRef.current = false; // consumed — the NEXT change is a real one
      return;
    }
    if (overflowed) setOverflowed(false);
    if (outcomeKind === "empty") {
      setOutcomeKind(null);
      setPhase((p) => (p === "outcome" ? "idle" : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const boxLabelSuffix = boxLabel ? `, into ${boxLabel}` : "";
  // R3-F1 (blocker) — see `deriveVisibility`'s own header above: never
  // hide while `activeNow`, or U4's second recording (started while the
  // first transcript's status re-check is still pending) would lose its
  // Stop button the moment that re-check resolves unavailable.
  const { micOffDaily, hiddenEntirely, activeNow } = deriveVisibility(phase, localStatus, canRecord(), outcomeKind);

  if (hiddenEntirely && !activeNow) return null;

  const idFor = (part) => `${idPrefix}${part}`;

  // U4 (PR #969 review) — the drawing keeps "Talk instead of typing"
  // available beside EVERY outcome note (a transcript landing, heard
  // nothing, a failed send), so the player can dictate again and append
  // (state 11's own note: "Press Talk instead of typing to carry on. It
  // adds to the end."). The build used to hide the button the instant an
  // outcome rendered, leaving only Undo/an undrawn "OK" as a way back.
  // U3 — VoiceFirstUse also keeps the pill showing ABOVE the open
  // explainer, not replaced by it.
  // Never shown once the daily cap is spent (`micOffDaily`, state 11a) —
  // that state replaces the button with the grey `-off` strip instead.
  const showMicButton = !micOffDaily && (phase === "idle" || phase === "outcome" || phase === "explainer");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      {showMicButton && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
          <button
            type="button"
            data-testid={idFor("")}
            data-dc-id={idFor("")}
            aria-label={`Talk instead of typing${boxLabelSuffix}`}
            disabled={otherActive}
            onClick={onMicPress}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              minHeight: 44,
              padding: "0 14px",
              background: "var(--nw-surface)",
              border: `1px solid ${otherActive ? "var(--nw-line)" : "var(--nw-navy)"}`,
              borderRadius: "var(--nw-radius-pill)",
              color: otherActive ? "var(--nw-ink-muted)" : "var(--nw-navy)",
              fontWeight: 600,
              fontSize: 14,
              whiteSpace: "nowrap",
              cursor: otherActive ? "not-allowed" : "pointer",
              opacity: otherActive ? 0.6 : 1,
            }}
          >
            <Mic size={16} style={{ flexShrink: 0 }} aria-hidden="true" />
            Talk instead of typing
          </button>
        </div>
      )}

      {micOffDaily && phase !== "explainer" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            data-testid={idFor("-off")}
            data-dc-id={idFor("-off")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              minHeight: 44,
              padding: "0 12px",
              background: "var(--nw-line-faint, #F1F5F9)",
              border: "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-control)",
              color: "var(--nw-ink-faint, #94A3B8)",
              fontSize: 13,
            }}
          >
            <MicOff size={16} aria-hidden="true" />
            Talking is off until tomorrow
          </div>
          {/* U5 (PR #969 review) — state 11a's own sentence, verbatim,
              beside the `-off` pill. Skipped only for the one tick right
              after a successful transcript discovers the cap (11b): that
              moment shows the OTHER sentence, inside the outcome block
              below, next to the words that just landed. */}
          {!(phase === "outcome" && (outcomeKind === "inserted" || outcomeKind === "appended")) && (
            <div data-testid={idFor("-daily-limit")} data-dc-id={idFor("-daily-limit")} style={noteBoxStyle}>
              <Info size={16} style={navyIconStyle} aria-hidden="true" />
              <span>You&#39;ve used today&#39;s talking time. Typing still works, and talking is back tomorrow.</span>
            </div>
          )}
        </div>
      )}

      {phase === "explainer" && (
        <div
          data-testid={idFor("-explainer")}
          data-dc-id={idFor("-explainer")}
          style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "var(--nw-line-faint, #E8F1FB)", borderRadius: "var(--nw-radius-control)" }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--nw-ink)" }}>Talk instead of typing</div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Mic size={15} style={navyIconStyle} aria-hidden="true" />
            <span style={helpTextStyle}>Your phone will ask if Compass can use the microphone.</span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Info size={15} style={navyIconStyle} aria-hidden="true" />
            <span style={helpTextStyle}>We turn what you say into text, then delete the recording. Only the words you save are kept.</span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Info size={15} style={navyIconStyle} aria-hidden="true" />
            <span style={helpTextStyle}>We can mishear, especially in wind. Check the words before you save.</span>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Info size={15} style={navyIconStyle} aria-hidden="true" />
            <span style={helpTextStyle}>Up to 90 seconds at a time.</span>
          </div>
          <button
            type="button"
            data-testid={idFor("-privacy")}
            data-dc-id={idFor("-privacy")}
            onClick={() => setShowPrivacy((v) => !v)}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}
          >
            What happens to what you say
          </button>
          {showPrivacy && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>What happens to what you say</div>
              <span>Your phone records you until you press Stop, up to 90 seconds.</span>
              <span>The recording goes to our server, where a speech-to-text service turns it into text.</span>
              <span>Then we delete the recording. We don&#39;t keep it, and we don&#39;t listen to it.</span>
              <span>The words come back into the box. Nothing is saved until you press Save, and then only the words, the same as if you had typed them.</span>
              <span>If you&#39;re under 18, you can show this to a parent or guardian. It&#39;s the same for everyone: the recording is never kept.</span>
              <a
                data-testid={idFor("-privacy-link")}
                data-dc-id={idFor("-privacy-link")}
                href={privacyHref()}
                style={{ alignSelf: "flex-start", fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
              >
                Read the privacy notice
              </a>
            </div>
          )}
          <button
            type="button"
            data-testid={idFor("-explainer-continue")}
            data-dc-id={idFor("-explainer-continue")}
            onClick={onExplainerContinue}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 48, background: "var(--nw-navy)", color: "#FFFFFF", fontWeight: 700, fontSize: 15, border: "none", borderRadius: "var(--nw-radius-control)", cursor: "pointer" }}
          >
            <Mic size={16} aria-hidden="true" />
            Turn on the microphone
          </button>
          <button
            type="button"
            data-testid={idFor("-explainer-cancel")}
            data-dc-id={idFor("-explainer-cancel")}
            onClick={onExplainerCancel}
            style={{ alignSelf: "center", background: "none", border: "none", padding: 0, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}
          >
            Keep typing
          </button>
        </div>
      )}

      {phase === "permission" && (
        <div aria-live="polite" data-testid={idFor("-status")} data-dc-id={idFor("-status")} style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>
          Waiting for you to allow the microphone.
        </div>
      )}

      {phase === "denied" && (
        <div
          role="alert"
          data-testid={idFor("-denied")}
          data-dc-id={idFor("-denied")}
          style={{ ...alertNoteBoxStyle, flexDirection: "column" }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <TriangleAlert size={16} style={{ color: "var(--nw-alert)", flexShrink: 0 }} aria-hidden="true" />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span>The microphone is blocked, so we can&#39;t hear you.</span>
              <span>Typing still works. To talk instead, allow the microphone for this site in your browser&#39;s settings.</span>
            </div>
          </div>
          <button
            type="button"
            data-testid={idFor("-denied-help")}
            data-dc-id={idFor("-denied-help")}
            onClick={onDeniedTryAgain}
            style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, marginTop: 6, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer" }}
          >
            How to turn it on
          </button>
        </div>
      )}

      {phase === "recording" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div data-testid={idFor("-recording")} data-dc-id={idFor("-recording")} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 28 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "999px",
                background: "var(--nw-navy)",
                flexShrink: 0,
                animation: prefersReducedMotion() ? "none" : "nw-voice-pulse 1.4s ease-in-out infinite",
              }}
              aria-hidden="true"
            />
            <span data-testid={idFor("-status") + "-recording"} style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>
              Listening
            </span>
            <span style={{ flexGrow: 1, display: "flex", justifyContent: "center" }}>
              <span data-testid={idFor("-level")} data-dc-id={idFor("-level")} aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", gap: 3, height: 20 }}>
                {levels.map((h, i) => (
                  <span key={i} style={{ display: "inline-block", width: 3, height: h, borderRadius: 2, background: "var(--nw-navy)" }} />
                ))}
              </span>
            </span>
            <span data-testid={idFor("-timer")} data-dc-id={idFor("-timer")} style={{ display: "inline-flex", alignItems: "baseline", gap: 4, whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink)" }}>{fmtClock(elapsedS)}</span>
              <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>/ 1:30</span>
            </span>
          </div>
          <div aria-live="polite" id={idFor("-status") + "-live"} data-testid={idFor("-status")} data-dc-id={idFor("-status")} style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
            Recording. Press Stop when you&#39;re done.
          </div>
          {elapsedS >= WARN_AT_S && elapsedS < MAX_CLIP_S && (
            <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
              15 seconds left in this recording. We&#39;ll stop at 90 seconds and turn what you said into text.
            </div>
          )}
          <button
            type="button"
            ref={stopBtnRef}
            data-testid={idFor("-stop")}
            data-dc-id={idFor("-stop")}
            aria-label="Stop recording"
            onClick={stopRecording}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 48, width: "100%", background: "var(--nw-navy)", border: "none", borderRadius: "var(--nw-radius-control)", color: "#FFFFFF", fontWeight: 700, fontSize: 15, cursor: "pointer" }}
          >
            <Square size={12} fill="#FFFFFF" aria-hidden="true" />
            Stop
          </button>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>We turn it into text, then delete the recording.</span>
            <button
              type="button"
              data-testid={idFor("-cancel")}
              data-dc-id={idFor("-cancel")}
              onClick={deleteWhileRecording}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, minHeight: 44, padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-alert)", color: "var(--nw-alert)", fontWeight: 600, fontSize: 14, borderRadius: "var(--nw-radius-control)", whiteSpace: "nowrap", cursor: "pointer" }}
            >
              Delete it
            </button>
          </div>
        </div>
      )}

      {phase === "transcribing" && (
        <div aria-live="polite" data-testid={idFor("-status")} data-dc-id={idFor("-status")} style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>
          {slowTranscribing ? "Still turning it into text. The signal may be slow here." : "Turning it into text…"}
        </div>
      )}

      {phase === "outcome" && (outcomeKind === "inserted" || outcomeKind === "appended") && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {autoStoppedAtLimit && (
            <div data-testid={idFor("-limit")} data-dc-id={idFor("-limit")} style={noteBoxStyle}>
              <Info size={16} style={navyIconStyle} aria-hidden="true" />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span>That&#39;s the longest one recording can be, 90 seconds, so we stopped there and turned it into text.</span>
                <span>Press Talk instead of typing to carry on. It adds to the end.</span>
              </div>
            </div>
          )}
          {overflowed && (
            <div data-testid={idFor("-overflow")} style={noteBoxStyle}>
              <Info size={16} style={navyIconStyle} aria-hidden="true" />
              <span>That was more than this box holds, so the last part was left out. Check the end.</span>
            </div>
          )}
          <div data-testid={idFor("-check")} data-dc-id={idFor("-check")} style={noteBoxStyle}>
            <Info size={15} style={navyIconStyle} aria-hidden="true" />
            <span>
              {outcomeKind === "appended" ? "We added what you said after what you'd typed. " : ""}
              <strong>Check what we heard</strong>, numbers especially. Fix any word we got wrong. Only what&#39;s in the box is saved.
            </span>
            <button
              type="button"
              data-testid={idFor("-undo")}
              data-dc-id={idFor("-undo")}
              aria-label="Take out the words just added"
              onClick={onUndo}
              style={{ marginLeft: "auto", flexShrink: 0, background: "none", border: "none", padding: 0, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              Undo
            </button>
          </div>
          {micOffDaily && (
            <div data-testid={idFor("-daily-limit")} data-dc-id={idFor("-daily-limit")} style={noteBoxStyle}>
              <Info size={16} style={navyIconStyle} aria-hidden="true" />
              <span>That&#39;s today&#39;s talking time used. What you said is in the box. Typing still works.</span>
            </div>
          )}
        </div>
      )}

      {/* U6 (PR #969 review) — no dismiss button (undrawn). The note
          clears itself when the player types, talks again, or presses
          Undo (there is none to press here, so in practice: types or
          talks again — the effect above and `beginRecording`'s own
          `clearOutcomeNotes()` cover both). */}
      {phase === "outcome" && outcomeKind === "empty" && (
        <div data-testid={idFor("-error")} data-dc-id={idFor("-error")} style={noteBoxStyle}>
          <Info size={16} style={navyIconStyle} aria-hidden="true" />
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flexGrow: 1 }}>
            <span>We didn&#39;t catch any words. Try again closer to the phone, or type it.</span>
            <span>No minutes were used.</span>
          </div>
        </div>
      )}

      {phase === "outcome" && outcomeKind === "too_long_server" && (
        <div data-testid={idFor("-limit")} data-dc-id={idFor("-limit")} style={noteBoxStyle}>
          <Info size={16} style={navyIconStyle} aria-hidden="true" />
          <span>That&#39;s the longest one recording can be, 90 seconds, so we stopped there and turned it into text.</span>
        </div>
      )}

      {phase === "outcome" && (outcomeKind === "network" || outcomeKind === "server" || outcomeKind === "too_slow" || outcomeKind === "rate_limited") && (
        <div role="alert" data-testid={idFor("-error")} data-dc-id={idFor("-error")} style={{ ...alertNoteBoxStyle, flexDirection: "column" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <TriangleAlert size={16} style={{ color: "var(--nw-alert)", flexShrink: 0 }} aria-hidden="true" />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {outcomeKind === "network" && <span>We couldn&#39;t turn that into text. The signal dropped before it reached us.</span>}
              {outcomeKind === "server" && <span>We couldn&#39;t turn that into text. Something went wrong on our side.</span>}
              {outcomeKind === "too_slow" && <span>We couldn&#39;t turn that into text. It took too long, probably a weak signal.</span>}
              {outcomeKind === "rate_limited" && <span>You&#39;ve talked a lot in the last hour. Give it a few minutes, or type instead.</span>}
              {outcomeKind !== "rate_limited" && (
                <span>
                  Your recording is kept on this page only, so you can send it again. If you leave the page, it&#39;s gone. It isn&#39;t saved
                  anywhere else. No minutes were used.
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              type="button"
              data-testid={idFor("-retry")}
              data-dc-id={idFor("-retry")}
              onClick={onSendAgain}
              style={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center", gap: 6, minHeight: 48, padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-navy)", color: "var(--nw-navy)", fontWeight: 600, fontSize: 14, borderRadius: "var(--nw-radius-control)", cursor: "pointer" }}
            >
              Send it again
            </button>
            <button
              type="button"
              data-testid={idFor("-discard")}
              data-dc-id={idFor("-discard")}
              onClick={onDiscardFailed}
              style={{ display: "inline-flex", flex: 1, alignItems: "center", justifyContent: "center", gap: 6, minHeight: 44, padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-alert)", color: "var(--nw-alert)", fontWeight: 600, fontSize: 14, borderRadius: "var(--nw-radius-control)", whiteSpace: "nowrap", cursor: "pointer" }}
            >
              Delete it
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default DictationControl;
